import React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import {
  useGetEvent,
  useGetFace,
  useGetLists,
} from '@vlabs/api/luna-api/client';
import { DetectionIcon } from '@vlabs/icons';
import { MultiSample } from '@vlabs/shared/components/sample/MultiSample';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { Control } from '@vlabs/uikit';
import { routes } from '@vlapps/point/src/app/routes';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { CardLink } from './CardLink';
import st from './MatchedObjectInfo.module.sass';

const getAvatarURL = (avatar) => {
  if (avatar?.startsWith('http') || avatar?.startsWith('//')) {
    return avatar;
  }
  return `/api/lp5${avatar}`;
};

function MatchedObjectInfo({
  topMatch,
}) {
  const { t } = useTranslation();

  const matchType = topMatch?.face_id ? 'face' : 'event';
  const matchedObjectId = topMatch?.face_id ?? topMatch?.event_id;

  const getFace = useGetFace(topMatch?.face_id ?? '', undefined, {
    query: { enabled: matchType === 'face' && !!matchedObjectId },
  });
  const getEvent = useGetEvent(topMatch?.event_id ?? '', undefined, {
    query: { enabled: matchType === 'event' && !!matchedObjectId },
  });
  const matchedObject = matchType === 'face' ? getFace.data : getEvent.data;
  const listIds = matchedObject?.lists ?? [];
  const getLists = useGetLists({ list_ids: listIds }, { query: { enabled: listIds.length > 0 } });

  if (!matchedObject) return null;

  const faceSampleId = matchType === 'event'
    ? matchedObject.face_detections?.find(({ sample_id }) => sample_id)?.sample_id
    : undefined;
  const bodySampleId = matchType === 'event'
    ? matchedObject.body_detections?.find(({ sample_id }) => sample_id)?.sample_id
    : undefined;

  let faceSampleUrl;
  if (matchType === 'face' && matchedObject.avatar) {
    faceSampleUrl = getAvatarURL(matchedObject.avatar);
  } else if (faceSampleId) {
    faceSampleUrl = getLunaSampleUrl('faces', faceSampleId);
  }
  const bodySampleUrl = getLunaSampleUrl('bodies', bodySampleId);

  const renderLists = () => {
    if (!listIds.length) return '-';

    return listIds.map((listId) => {
      const list = getLists.data?.lists?.find(({ list_id }) => list_id === listId);

      return (
        <Control.Link
          className={st.ListLink}
          key={listId}
          to={generatePath(routes.lists.read, { listId })}
        >
          {list?.user_data ?? listId}
        </Control.Link>
      );
    });
  };

  return (
    <div className={st.Root}>
      <p className={st.Title}>{t('lunapoint:person_card.match')}</p>

      <div className={st.Content}>
        <div className={st.Sample}>
          <MultiSample
            className={st.MultiSample}
            data-testid="infoTab.matchSection.matchSample"
            primary={{
              imageUrl: faceSampleUrl,
              type: 'face',
            }}
            secondary={{
              imageUrl: bodySampleUrl,
              type: 'body',
            }}
          />
        </div>

        <div className={st.InfoBlock}>
          <SimilarityTag
            data-testid="infoTab.matchSection.similarityTagType"
            icon={<DetectionIcon />}
            size="large"
            title={t(`lunapoint:person_card.${matchType === 'face' ? 'face' : 'event'}`)}
            value={topMatch.similarity}
          />

          <div className={st.Data}>
            <div className={st.Header}>{t('lunapoint:person_card.card_id')}</div>
            <div
              className={st.Value}
              data-testid="infoTab.matchSection.externalId"
            >
              <CardLink cardId={matchedObject.external_id} />
            </div>

            <div className={st.Header}>{t('lunapoint:person_card.lists')}</div>
            <div
              className={st.Value}
              data-testid="infoTab.matchSection.listId"
            >
              <div className={st.ListsList}>
                {renderLists()}
              </div>
            </div>

            <div className={st.Header}>
              {t('lunapoint:person_card.information')}
            </div>
            <div
              className={st.Value}
              data-testid="infoTab.matchSection.userData"
            >
              {matchedObject.user_data ?? '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { MatchedObjectInfo };
